<script lang="ts">
import { defineComponent, inject, reactive } from 'vue'
import stateInitializer from '@/models/stateInitializer'
import PageHeadingDefault from '@/components/_core/PageHeadingDefault.vue'
import ButtonSubmit from '@/components/button/ButtonSubmit.vue'
import InputFormMailAddress from '@/components/lead/InputFormMailAddress.vue'
import InputPasswordForm from '@/components/form/InputPasswordForm.vue'
import OAuthOptions from '@/components/sns/OAuthOptions.vue'
import {
  newUserPasswordPath,
  newUserRegistrationPath,
} from '@/generated/railsRoutes'

export default defineComponent({
  name: 'UsersSignInMain',

  components: {
    PageHeadingDefault,
    ButtonSubmit,
    InputFormMailAddress,
    InputPasswordForm,
    OAuthOptions,
  },

  setup() {
    // constants
    const EXTERNAL_URL = inject<Record<string, string>>('EXTERNAL_URL')!

    // data
    interface State {
      signIn: {
        form: Form,
        input: {
          email: InputText,
          password: InputText,
        },
      },
    }
    const state: State = reactive({
      signIn: {
        form: {
          action: '',
          method: 'post',
          name: '',
        },
        input: {
          email: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
              email: true,
            },
          },
          password: {
            value: '',
            name: '',
            validationScheme: {
              required: true,
            },
          },
        },
      },
    })
    stateInitializer(state, 'main')

    return {
      // constants
      EXTERNAL_URL,

      // routes
      newUserPasswordPath,
      newUserRegistrationPath,

      // data
      state,
    }
  },
})
</script>

<template>
  <section class="section">
    <div class="l-cols__signIn">
      <div class="l-cols__signIn--col is-primary">
        <section class="section">
          <PageHeadingDefault
            label="ログイン"
            small />

          <div class="o-label__signInIntroduction">
            <p class="o-label__signInIntroduction--item">
              すべてのコンテンツをご利用するにはログインが必要です。<br>
              <a
                class="a"
                :href="newUserRegistrationPath()">
                会員登録はこちら
              </a>
            </p>
          <!-- /.o-label__signInIntroduction --></div>

          <FormDefault
            :action="state.signIn.form.action"
            :method="state.signIn.form.method"
            :name="state.signIn.form.name">
            <InputFormMailAddress
              v-model="state.signIn.input.email.value"
              :name="state.signIn.input.email.name"
              :validation-scheme="state.signIn.input.email.validationScheme"
              :namespace="state.signIn.form.name"
              placeholder=" "
              header="メールアドレス" />

            <InputPasswordForm
              v-model="state.signIn.input.password.value"
              :name="state.signIn.input.password.name"
              :validation-scheme="state.signIn.input.password.validationScheme"
              :namespace="state.signIn.form.name"
              autocomplete="current-password" />

            <ButtonSubmit
              name="commit"
              :is-link="false"
              is-submit
              is-font-small
              is-large
              has-shadow>
              ログインする
            </ButtonSubmit>
          </FormDefault>

          <ul class="ul">
            <li class="li u-text__center">
              <a
                class="a u-text__small"
                :href="newUserPasswordPath()">
                パスワードをお忘れですか？
              </a>
            </li>
            <li class="li u-text__center">
              <a
                class="a u-text__small"
                :href="EXTERNAL_URL.bizhint_help_url">
                その他の質問はこちら
              </a>
            </li>
          </ul>

          <OAuthOptions />
        </section>
      <!-- /.l-cols__signIn col --></div>

      <div class="l-cols__signIn--col is-secondary">
        <section class="f-section__serviceIntroduction">
          <div class="f-section__serviceIntroduction--body">
            <h2 class="h2 u-text__xx-huge u-text__bold">
              BizHintへようこそ
            </h2>
            <p class="p">
              BizHint（ビズヒント）は、全国の企業決裁者が抱える悩みに、正面から向き合い、課題解決に繋がる情報を発信し続けてまいります。
            </p>
          <!-- /.f-section__serviceIntroduction body --></div>
        <!-- /.f-section__serviceIntroduction --></section>
      <!-- /.l-cols__signIn col --></div>

    <!-- /.l-cols__signIn --></div>
  </section>
</template>

<style lang="sass" scoped>
// layout
.l-cols__signIn
  $root: &
  $primary-width-px: 464
  $secondary-width-px: $global-contents-width-pc - $primary-width-px
  display: flex
  position: relative
  overflow: hidden
  margin-top: #{$global-section-prop-pc}px
  background-color: rgba($bg-white, 1)
  border-radius: 10px

  &::before
    $image-width-px: 640
    $image-height-px: 424
    $image-position-bottom: 32
    content: ""
    width: getRelativeValue($image-width-px, $global-contents-width-pc)
    display: block
    position: absolute
    left: 0
    bottom: #{$image-position-bottom}px
    padding-top: getRelativeValue($image-height-px, $global-contents-width-pc)
    background:
      image: url(@/images/account/bg-signIn_01.png)
      repeat: no-repeat
      size: contain

  & &--col
    position: relative

    &.is-primary
      $padding-px: 40
      width: getRelativeValue($primary-width-px, $global-contents-width-pc)
      padding: getRelativeValue($padding-px, $global-contents-width-pc)
      order: 1

    &.is-secondary
      $padding-px: 96
      width: getRelativeValue($secondary-width-px, $global-contents-width-pc)
      padding: getRelativeValue($padding-px, $global-contents-width-pc)

  +breakpoint
    $image-width-px: 1280
    $image-height-px: 848
    display: block
    overflow: visible
    margin-top: 0
    padding-bottom: getRelativeValue(360, $global-contents-width-phablet)
    background-color: transparent
    border-radius: 0

    &::before
      content: ""
      width: 100%
      bottom: 0
      margin:
        left: -#{$global-prop-sp}px
        bottom: -#{$global-prop-sp}px
      padding-top: getRelativeValue($image-height-px, $image-width-px)

    #{$root}--col
      &.is-primary
        width: auto
        background-color: rgba($bg-white, 1)
        border-radius: $border-radius

      &.is-secondary
        display: none

// object
.o-label__signInIntroduction
  margin-bottom: 1.5em

  & &--item
    font-size: $font-small
</style>
